// Eskom PARCC — KPI card primitives.
//
// `KpiCard` wraps the monolith's `.kpi` block (`.label` / `.value` / optional
// `.sub`); `KpiGrid` wraps the responsive `.kpis .grid` container the monolith
// used to lay a row of them out.
import type { ReactNode } from 'react';

// Currency from `formatRand` uses non-breaking spaces (U+00A0) as thousand
// separators, which is right for tables/exports (a value never splits across a
// cell) but means a large figure like "R 322 719 486 940" cannot wrap inside a
// KPI card and bursts out of it. For the headline value only, swap those for
// ordinary spaces so the number wraps cleanly at group boundaries; the CSS
// `overflow-wrap: anywhere` backstop still contains pathological cases.
function wrappableValue(value: ReactNode): ReactNode {
  return typeof value === 'string' ? value.replace(/\u00A0/g, ' ') : value;
}

export function KpiCard({
  label,
  value,
  sub,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** Extra class(es) composed onto `.kpi` (e.g. `kpi-outstanding`). */
  className?: string;
}): React.JSX.Element {
  return (
    <div className={className ? `kpi card ${className}` : 'kpi card'}>
      <div className="label">{label}</div>
      <div className="value">{wrappableValue(value)}</div>
      {sub !== undefined && <div className="sub">{sub}</div>}
    </div>
  );
}

export function KpiGrid({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="kpis grid">{children}</div>;
}
